/* eslint-disable no-plusplus */
/* eslint-disable react/no-unknown-property */
import {
  Avatar, Stack, Typography,
  useColorScheme,
} from '@mui/joy';
import React, { useMemo } from 'react';
import rob from 'assets/arabRob.png';
import robMin from 'assets/arabRob-min.png';
import { TbTerminal2 } from 'react-icons/tb';
import { Parallax } from 'react-scroll-parallax';
import ProgressiveImage from 'components/ProgressiveImage';
import { Default, Mobile, useMobileMode } from '../Responsive';

export default function OpenSource() {
  const { colorScheme } = useColorScheme();
  const dark = useMemo(() => colorScheme === 'dark', [colorScheme]);

  const mobile = useMobileMode();

  const flickerAnimation = useMemo(() => ({
    '@keyframes flicker': Object.fromEntries(
      [...Array(10).keys()].map(() => Math.random() * 100).sort().map((p) => [
        [`${p - 1}%`, { opacity: 1 }],
        [`${p}%`, { opacity: 0 }],
        [`${p + 1}%`, { opacity: 1 }],
      ]).flat(),
    ),
  }), []);

  return (
    <Stack
      p="20px"
      sx={{ position: 'relative' }}
      marginBottom={mobile ? '5rem' : 0}
    >
      <Stack sx={{
        width: '100%',
        height: mobile ? '48rem' : '400px', // gonna change it
        // overflow: 'hidden',
      }}
      >
        <Stack
          sx={{
            position: 'absolute',
            top: '-20px',
            gap: 3,
            textAlign: mobile ? 'center' : 'right',
            ...(mobile ? {
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100%',
              alignItems: 'center',
            } : {
              right: '0',
              alignItems: 'flex-end',
            }),
          }}
        >
          <Mobile>
            <Avatar
              color="success"
              sx={(theme) => ({
                position: 'relative',
                border: 'none',
                outline: `2px solid ${theme.palette.success[500]}`,
                boxShadow: `0 0 40px 5px rgba(${theme.palette.success.mainChannel} / 0.4)`,
                overflow: 'visible',
                marginTop: '3rem',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-5rem',
                  height: '5rem',
                  width: '2px',
                  background: `linear-gradient(to bottom, transparent, ${theme.palette.success[400]})`,
                },
              })}
            >
              <TbTerminal2 />
            </Avatar>
          </Mobile>
          <Typography
            level="h2"
            sx={{
              position: 'relative',
            }}
          >
            <Typography
              color="success"
              fontWeight="xl"
            >
              Software.
            </Typography>
            {mobile ? <br /> : ' '}
            Command
            {' '}
            <Typography sx={(theme) => ({
              ...flickerAnimation,
              ...(dark ? {
                textShadow: `0 0 1px ${theme.palette.text.primary}, 0 0 .5rem ${theme.palette.text.primary}, 0 0 3rem ${theme.palette.text.secondary}`,
                color: 'white',
                animation: 'flicker 5s infinite',
                animationDelay: `${Math.random() * 2}s`,
                fontWeight: '300',
              } : {
                color: theme.palette.text.primary,
                fontWeight: '900',
                fontFamily: '"Lobster", sans-serif',
              }),
              padding: '3rem',
              margin: '-3rem',
            })}
            >
              {dark ? 'Robust' : 'Scalable'}
            </Typography>
            {' '}
            Architectures.
            <Default>
              <Avatar
                color="success"
                sx={(theme) => ({
                  position: 'absolute',
                  top: '0',
                  right: '-50px',
                  transform: 'translateX(50%)',
                  border: 'none',
                  outline: `2px solid ${theme.palette.success[500]}`,
                  boxShadow: `0 0 40px 5px rgba(${theme.palette.success.mainChannel} / 0.4)`,
                })}
              >
                <TbTerminal2 />
              </Avatar>
            </Default>
          </Typography>
          <Typography
            level="h5"
            textColor="text.tertiary"
            sx={{
              maxWidth: '600px',
              fontFamily: 'Handjet',
            }}
          >
            Raised in
            {' '}
            <Typography textColor="text.primary">
              Zagazig, Egypt
            </Typography>
            , I moved to the U.S. on a scholarship to
            {' '}
            dive deep into the systems that power the world.
            I understand the machine—from
            {' '}
            <Typography textColor="text.primary">
              hardware architecture
            </Typography>
            {' '}
            and low-level systems to scalable, high-performance
            {' '}
            <Typography textColor="text.primary">
              backend systems
            </Typography>
            . I build with
            {' '}
            <Typography textColor="text.primary">
              algorithmic rigor
            </Typography>
            , ensuring the foundation is as strong as the interface.
            At my core, I simply love to
            {' '}
            <Typography textColor="text.primary">
              design
            </Typography>
            —algorithms, architectures, interfaces, and systems.
          </Typography>
        </Stack>
        <Parallax
          speed={10}
          opacity={[0, 1]}
          easing="ease"
          disabled={mobile}
          style={mobile ? {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '34rem', // change
            zIndex: 1,
            animation: 'float 20s ease-in-out infinite',
          } : {
            position: 'absolute',
            right: 'calc(350px + 30%)',
            top: '2rem',
            zIndex: 1,
            animation: 'float 20s ease-in-out infinite',
          }}
        >
          <ProgressiveImage
            src={rob}
            placeholder={robMin}
            alt="Darth rob"
            style={{
              ...(mobile ? {
                height: '300px',
              } : {
                height: '475px',
              }),
              filter: 'drop-shadow(0 -20px 20px hsla(185, 74%, 41%, 0.299)) drop-shadow(0 20px 20px hsla(7, 57%, 51%, 0.364))',
            }}
          />
        </Parallax>
      </Stack>

    </Stack>
  );
}
